import { useState } from 'react';
import { Button } from 'react-bootstrap';
import { NavWrapper, NavItemWrapper, NavItemStaticWrapper } from './styles';
import { IDonorProfileHeaderInfo } from '@nkr/models/index';
import { DonorAction, EntityType, HistogeneticsEntityType, MenuLocation } from '@nkr/enums/index';
import ActionButtonRenderer from '../swap-donors/components/action-button-renderer';

interface IProps {
    data: IDonorProfileHeaderInfo,
    activeKey: string,
    isAdmin: boolean,
    isSuper: boolean,
    isTransplanted: boolean,
    canOrderCheekSwab: boolean,
    centerId: number,
    vhId?: number,
    hasChanges: boolean,
    onTabClick: (href: string) => void,
    onSave: () => void,
    onGetCheekOrderData: () => void,
    onUtilizeExistingCheekSwab: () => void,
    onShowDeleteModal?: () => void,
    onSendSelfRegistrationInvite: () => void,
    onShowDonorConversion: () => void,
    onPrintRequisition: (orderId: number, entityTypeId: number) => void,
    onStartExpertSystem?: () => void
}

export const getDonorProfileTabMap = (isAdp: boolean, isTransplanted: boolean) => {
    return [
        { eventKey: 'DONOR-DATA', href: '/donor-data', title: 'Donor Data', display: true },
        { eventKey: 'DONOR-ADDRESS', href: '/donor-address', title: 'Donor Address', display: true },
        { eventKey: 'MEDICAL-DETAILS', href: '/medical-details', title: 'Medical Details', display: true },
        { eventKey: 'HLA-INFO', href: '/hla-info', title: 'HLA Info', display: true },
        { eventKey: 'RECORDS', href: '/records', title: 'CT/Records', display: true },
        { eventKey: 'ACTIVITY', href: '/activity', title: 'Activity', display: true },
        { eventKey: 'VOUCHERS', href: '/vouchers', title: 'Vouchers', display: isAdp },
        { eventKey: 'POST-TRANSPLANT', href: '/post-transplant', title: 'Post Transplant', display: isTransplanted },
    ];
}

const DonorProfileNav = (props: IProps) => {
    const [menuDisplayed, setMenuDisplayed] = useState(false);

    const handleMenuToggle = (show: boolean) => {
        setMenuDisplayed(show);
    };

    const handleTabClick = (e: any) => {
        e.preventDefault();
        props.onTabClick(e.currentTarget.href);
    };

    const handleItemSelected = (action: DonorAction) => {
        if (action == DonorAction.Delete) {
            props.onShowDeleteModal();
        }

        if (action == DonorAction.View) {
        }

        if (action == DonorAction.SendEmail) {
            props.onSendSelfRegistrationInvite();
        }

        if (action == DonorAction.ConvertDonorType) {
            props.onShowDonorConversion();
        }

        if (action == DonorAction.OrderTissueTyping) {
            props.onGetCheekOrderData();
        }

        if (action == DonorAction.UtilizeBulkCheekSwabKit) {
            props.onUtilizeExistingCheekSwab();
        }

        if (action == DonorAction.PrintRequisition) {
            props.onPrintRequisition(props.data.requisitionId, HistogeneticsEntityType.Donor)
        }

        if (action == DonorAction.StartExpertSystem && props.onStartExpertSystem) {
            props.onStartExpertSystem();
        }
    };

    if (props.data == null)
        return null;

    const hrefPrefix = props.isAdmin ? `/admin/donor/${props.data.id}/profile` : `/center/${props.centerId}/donor/${props.data.id}/profile`;

    const tabs = getDonorProfileTabMap(props.data.isAdp, props.isTransplanted);

    const voucherHolderId = props.vhId != null ? props.vhId : 0;

    return (
        <>
            <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <NavWrapper bsStyle="tabs" activeKey={props.activeKey} style={{ width: 'initial', borderBottom: '0 none' }}>
                    {/* {props.data.alias &&
                        <NavItemStaticWrapper eventKey={props.activeKey}>
                            Alias: {props.data.alias}
                        </NavItemStaticWrapper>
                    } */}

                    {tabs.filter(x => x.display).map(tab =>
                        <NavItemWrapper eventKey={tab.eventKey} href={hrefPrefix + tab.href} onClick={handleTabClick}>
                            {tab.title}
                        </NavItemWrapper>
                    )}

                    {props.activeKey == 'VOUCHER-EDIT' && voucherHolderId != 0 && (
                        <>
                            <NavItemWrapper active={'VOUCHER-EDIT'} href={`/center/${props.centerId}/donor/${props.data.id}/voucher/${props.vhId}`}>
                                Edit Voucher
                            </NavItemWrapper>
                        </>
                    )}

                    {props.activeKey == 'VOUCHER-EDIT' && voucherHolderId == 0 && (
                        <>
                            <NavItemWrapper active={'VOUCHER-EDIT'} href={`/center/${props.centerId}/donor/${props.data.id}/voucher/add`}>
                                Add Voucher
                            </NavItemWrapper>
                        </>
                    )}
                </NavWrapper>

                {props.hasChanges && (
                    <div style={{ backgroundColor: 'greenyellow', color: '#333', padding: 6, textAlign: 'center', lineHeight: 1, fontSize: 12, borderRadius: 4 }}>
                        This page contains<br />pending changes
                    </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ paddingRight: 5 }}>
                        <Button bsStyle='primary' disabled={props.hasChanges == false} onClick={props.onSave}>Save Changes</Button>
                    </div>
                    <ActionButtonRenderer
                        donorId={props.data.id}
                        donorCenterId={props.data.donorCenterId}
                        donationCenterId={props.data.donationCenterId}
                        canDelete={props.data.canDelete}
                        canConvert={props.data.canConvert}
                        isAdp={props.data.isAdp}
                        orderId={props.data.requisitionId}
                        statusId={props.data.statusId}
                        wageAndTravelRequested={props.data.wageAndTravelRequest}
                        recipientId={props.data.recipientId}
                        recipientType={props.data.recipientType}
                        recipientCenterId={props.data.recipientCenterId}
                        registrationId={props.data.registrationId}
                        centerId={props.centerId}
                        isAdmin={props.isAdmin}
                        isSuper={props.isSuper}
                        canOrderCheekSwab={props.canOrderCheekSwab}
                        isExpertSystemParticipant={props.data.isExpertSystemParticipant}
                        isInExpertSystem={props.data.isInExpertSystem}
                        menuLocation={MenuLocation.Profile}
                        onMenuToggle={handleMenuToggle}
                        onItemSelected={handleItemSelected}
                    />
                </div>
            </div>
        </>
    );
}

export default DonorProfileNav;